
import { type FC } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface SelectOption {
  value: string
  label: string
  /** Optional muted styling for the label */
  muted?: boolean
}

interface SelectParamProps {
  /** Label text */
  label: string
  /** Current selected value */
  value: string
  /** Available options */
  options: SelectOption[]
  /** Called with new value */
  onChange: (value: string) => void
  /** Placeholder text for SelectValue */
  placeholder?: string
  /**
   * Layout variant:
   * - "flex": label flex-1, trigger flex-1 (shader style)
   * - "fixed": label with optional icon, trigger w-[180px] (image style)
   */
  variant?: 'flex' | 'fixed'
  /** Icon element to render before the label (for "fixed" variant) */
  icon?: React.ReactNode
  /** Additional classes on the select trigger */
  triggerClassName?: string
}

export const SelectParam: FC<SelectParamProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  variant = 'flex',
  icon,
  triggerClassName,
}) => {
  if (variant === 'fixed') {
    return (
      <div className="flex items-center justify-between px-2">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm">{label}</span>
        </div>
        <Select value={value} onValueChange={onChange}>
          <SelectTrigger className={triggerClassName ?? 'w-[180px] h-7'}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.muted ? (
                  <span className="text-muted-foreground">{opt.label}</span>
                ) : (
                  opt.label
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    )
  }

  // variant === 'flex'
  return (
    <div className="flex items-center gap-2 px-2">
      <span className="text-sm flex-1">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={triggerClassName ?? 'h-7 flex-1'}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.muted ? (
                <span className="text-muted-foreground">{opt.label}</span>
              ) : (
                opt.label
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
